import React, {useEffect, useMemo, useRef, useState} from "react";
import { useTranslation } from 'react-i18next';
import {Radio, Checkbox} from "antd";

// Components
import TextWithLinks from "sharedComponents/TextWithLinks";

// Utils
import validateExplanationField from "utils/validateExplanationField";
import parseJsonSafely from "utils/parseJsonSafely";

// Hooks
import useDropoutSurveyField from "Hooks/useDropoutSurveyField";
import {findDOMNode} from "react-dom";


const SingleOrMultiChoice = ({type, fieldData, getAnswer, mandatoryRefs, handleChangeSurveyCompletionStatus = null, disabled = false}) => {
    const [showError, SetShowError] = useState('');
    const [singleChoice, SetSingleChoice] = useState(null);
    const [multiChoice, SetMultiChoice] = useState(fieldData?.answers?.answer || null);
    const [showExplanation, SetShowExplanation] = useState(false);
    const { t } = useTranslation();
    const explanationRef = useRef();
    const singleRef = useRef();
    const multiRef = useRef();

    const typeContent = useMemo(() => parseJsonSafely(fieldData.type_content), [fieldData]);

    const multiCheckOptions = typeContent?.map(item => item.name);

    useDropoutSurveyField(singleChoice, multiChoice, handleChangeSurveyCompletionStatus);

    useEffect(() => {
        if (getAnswer.constructor !== Boolean) {
            getAnswer(prevState => ({
                ...prevState,
                [fieldData.id]: {
                    answer: type ? multiChoice || '' : singleChoice || '', explanation: explanationRef.current?.value || ''
                },
            }));
            !(!!type ? multiChoice : singleChoice) && !!fieldData.mandatory && SetShowError('Required');
        }
    }, [getAnswer, type, multiChoice, singleChoice, fieldData]);

    useEffect(() => {
        let fieldNode = findDOMNode(singleRef.current);
        if (type) {
            fieldNode = findDOMNode(multiRef.current);
        }
        if (fieldData.mandatory) {
            if (Array.isArray(mandatoryRefs) && !mandatoryRefs.includes(fieldNode)) {
                mandatoryRefs.push(fieldNode);
            }
        }
    }, [fieldData]);

    const handleSelectSingle = (value) => {
        showError && SetShowError('');
        SetSingleChoice(value.target.value);
        fieldData.explanation && SetShowExplanation(true);

        if (!value?.target?.value) {
            const fieldNode = findDOMNode(singleRef.current);
            if (fieldData.mandatory) {
                if (Array.isArray(mandatoryRefs) && !mandatoryRefs.includes(fieldNode)) {
                    mandatoryRefs.unshift(fieldNode);
                }
            }
        }
    };

    useEffect(() => {
        if (fieldData?.field_type === "Single choice group") {
            SetSingleChoice(fieldData?.answers?.answer);
        } else {
            fieldData?.answers?.answer && SetMultiChoice(JSON.parse(fieldData?.answers?.answer));
        }
    }, [])

    useEffect(() => {
        if (fieldData?.answers?.answer) {
            SetShowExplanation(!!fieldData?.answers?.explanation);
            explanationRef.current.value = fieldData?.answers?.explanation;
        }
    }, []);

    const handleClick = (event) => {
        if (event.target.value === singleChoice) {
            SetSingleChoice('');
            SetShowExplanation(false);
        } else {
            SetSingleChoice(event.target.value);
        }
    }

    const handleSelectMulti = (value) => {
        value.length && SetShowError('');
        SetMultiChoice(value.length ? value : null);
        fieldData.explanation && SetShowExplanation(!!value.length);

        if (!value.length) {
            const fieldNode = findDOMNode(multiRef.current);
            if (fieldData.mandatory) {
                if (Array.isArray(mandatoryRefs) && !mandatoryRefs.includes(fieldNode)) {
                    mandatoryRefs.unshift(fieldNode);
                }
            }
        }
    };

    const handleChangeExplanation = (e) => {
        SetShowError(validateExplanationField(e.target.value));
    };

    return <div
        ref={type ? multiRef : singleRef}
        className={`single_sect ${showError ? "invalid" : ""}`}
    >
        <h3 className="single_sect_primary_text">
            {fieldData.label}
            {!!fieldData.mandatory && <sup>*</sup>}
        </h3>
        <TextWithLinks className="single_sect_secondary_text">
            {fieldData.description}
        </TextWithLinks>

        <div className="rate_with_explanation">
            {!type ? <Radio.Group
                value={singleChoice}
                onChange={handleSelectSingle}
                className={`single_choice_selection ${!singleChoice && 'unchecked_choice'}`}
            >
                {typeContent?.map(item => <Radio
                    onClick={handleClick}
                    className="choice_item"
                    key={item.name}
                    value={item.name}
                    disabled={disabled}
                >
                    {item.name}
                </Radio>)}
            </Radio.Group> : <Checkbox.Group
                value={multiChoice}
                className="multi_choice_selection"
                options={multiCheckOptions}
                onChange={handleSelectMulti}
                disabled={disabled}
            />}
            <div className={`input_field hidden_input_field ${showExplanation ? "show" : "hide"}`}>
                <input
                    ref={explanationRef}
                    onChange={handleChangeExplanation}
                    type="text"
                    placeholder={t("Explain your choice (optional)...")}
                    disabled={disabled}
                />
            </div>
        </div>
        <div className="error_sect">
            {showError}
        </div>
    </div>
}

export default React.memo(SingleOrMultiChoice);
